// src/services/dashboardService.ts
import apiClient from '../api/apiClient';

const TENANT_ID = process.env.REACT_APP_TENANT_ID ?? '1';
const SUMMARY_PATH = '/admin/dashboard/summary';
const IMAGES_PATH = '/admin/dashboard/images';

/**
 * Calls GET {baseURL}/admin/dashboard/summary?tenantId={TENANT_ID}
 * Returns a normalized record of numbers: { students: number, videos: number, ... }
 */
export async function getDashboardSummary(): Promise<Record<string, number>> {
    const url = `${SUMMARY_PATH}?tenantId=${TENANT_ID}`;
    try {
        const resp = await apiClient.get(url);
        console.log('[dashboardService] summary resp status =', resp.status);
        console.log('[dashboardService] summary resp data =', resp.data);
        const json = resp?.data ?? {};
        if (!json || typeof json !== 'object') {
            throw new Error('Invalid summary response');
        }
        const stats: Record<string, number> = {};
        for (const [k, v] of Object.entries(json)) {
            stats[k] = Number((v as any) ?? 0) || 0;
        }
        return stats;
    } catch (err: any) {
        console.error('[dashboardService] summary error ->', err?.response?.data ?? err?.message ?? err);
        const serverMessage = err?.response?.data?.message ?? err?.message;
        const e = new Error(`Failed to fetch dashboard summary${serverMessage ? `: ${serverMessage}` : ''}`);
        (e as any).original = err;
        throw e;
    }
}

// ── Dashboard carousel images (Spaces upload + Firebase RTDB list) ─────────────

const params = { tenantId: TENANT_ID };
const multipart = { params, headers: { 'Content-Type': 'multipart/form-data' } };

function unwrap(resp: any): string[] {
    return (resp?.data?.images ?? []) as string[];
}

function fail(action: string, err: any): never {
    const serverMessage = err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message;
    const e = new Error(`Failed to ${action}${serverMessage ? `: ${serverMessage}` : ''}`);
    (e as any).original = err;
    throw e;
}

/** GET the current dashboard image URLs, in display order. */
export async function getDashboardImages(): Promise<string[]> {
    try {
        return unwrap(await apiClient.get(IMAGES_PATH, { params }));
    } catch (err) {
        fail('load dashboard images', err);
    }
}

/** Upload a file to Spaces and append its URL to the dashboard list. */
export async function addDashboardImage(file: File): Promise<string[]> {
    const formData = new FormData();
    formData.append('file', file);
    try {
        return unwrap(await apiClient.post(IMAGES_PATH, formData, multipart));
    } catch (err) {
        fail('add dashboard image', err);
    }
}

/** Replace the image at `index` with a newly uploaded file. */
export async function replaceDashboardImage(index: number, file: File): Promise<string[]> {
    const formData = new FormData();
    formData.append('file', file);
    try {
        return unwrap(await apiClient.put(`${IMAGES_PATH}/${index}`, formData, multipart));
    } catch (err) {
        fail('replace dashboard image', err);
    }
}

/** Remove the image at `index` from the list and from Spaces. */
export async function deleteDashboardImage(index: number): Promise<string[]> {
    try {
        return unwrap(await apiClient.delete(`${IMAGES_PATH}/${index}`, { params }));
    } catch (err) {
        fail('delete dashboard image', err);
    }
}

/** Persist a new display order. Must contain exactly the existing URLs. */
export async function reorderDashboardImages(urls: string[]): Promise<string[]> {
    try {
        return unwrap(await apiClient.put(`${IMAGES_PATH}/order`, { urls }, { params }));
    } catch (err) {
        fail('reorder dashboard images', err);
    }
}

